import { ListSubheader, MenuItem, Select } from '@mui/material';
import { RiskDateFieldOption } from '../../../Api/Admin/ProjectManagementConfigs/projectManagementConfigsApi';

const SOURCE_HEADINGS = { asset: 'Asset Fields', custom: 'Custom Fields' } as const;

// Select clones every child with role="option"; this wrapper swallows it so a heading isn't pickable.
const FieldGroupHeading = ({ children }: { children: string }) => (
  <ListSubheader role="presentation">{children}</ListSubheader>
);

export interface RiskDateFieldSelectProps {
  id: string;
  ariaLabel: string;
  value: string;
  options: RiskDateFieldOption[];
  onChange: (fieldId: string) => void;
  // Shown when the catalogue doesn't list the value; the API falls back to the raw id.
  orphanLabel?: string;
  unavailable?: boolean;
  error?: boolean;
  dataCy?: string;
}

const groupedOptions = (options: RiskDateFieldOption[]) => {
  const sources = (Object.keys(SOURCE_HEADINGS) as (keyof typeof SOURCE_HEADINGS)[]).filter((source) =>
    options.some((option) => option.source === source)
  );
  // A heading over the only list on offer is noise.
  const heading = sources.length > 1;

  return sources.flatMap((source) => [
    ...(heading ? [<FieldGroupHeading key={source}>{SOURCE_HEADINGS[source]}</FieldGroupHeading>] : []),
    ...options
      .filter((option) => option.source === source)
      .map((option) => (
        <MenuItem
          key={option.id}
          value={option.id}>
          {option.label}
        </MenuItem>
      ))
  ]);
};

export const RiskDateFieldSelect = ({
  id,
  ariaLabel,
  value,
  options,
  onChange,
  orphanLabel,
  unavailable,
  error,
  dataCy
}: RiskDateFieldSelectProps) => {
  // Without a matching item the value renders blank; "Unavailable" leads so truncation can't cut it.
  const orphan = !options.some((option) => option.id === value);
  const orphanName = orphanLabel ?? value;

  return (
    <Select
      inputProps={{ id, 'aria-label': ariaLabel }}
      size="small"
      className="w-56"
      value={value}
      error={error}
      disabled={options.length === 0}
      onChange={(e) => onChange(e.target.value)}
      style={{ fontSize: '.875rem' }}
      data-cy={dataCy}>
      {groupedOptions(options)}
      {orphan && (
        <MenuItem
          value={value}
          disabled>
          {unavailable ? `Unavailable: ${orphanName}` : orphanName}
        </MenuItem>
      )}
    </Select>
  );
};
